import React, { useState, useRef, useEffect } from 'react'
import { Edit } from 'lucide-react'
import { useThemeTokens } from '../../themes'

interface TimeInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  className?: string
}

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  autoFocus = false,
  className
}) => {
  const theme = useThemeTokens()
  const [isEditing, setIsEditing] = useState(autoFocus)
  const inputRef = useRef<HTMLInputElement>(null)

  // Convert 24-hour time to 12-hour AM/PM format
  const formatTimeDisplay = (time24: string): string => {
    if (!time24) return placeholder || '--:-- --'
    
    const [hours, minutes] = time24.split(':').map(Number)
    if (isNaN(hours) || isNaN(minutes)) return placeholder || '--:-- --'
    
    const period = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
  }

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.showPicker?.() // Show time picker if supported
    }
  }, [isEditing])

  // Auto-focus on mount if requested
  useEffect(() => {
    if (autoFocus) {
      setIsEditing(true)
    }
  }, [autoFocus])

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true)
    }
  }

  const handleBlur = () => {
    setIsEditing(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  // Button styles for display mode - wider for AM/PM format
  const buttonStyles: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: `1px solid transparent`,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.borderRadius.sm,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily,
    color: disabled ? theme.colors.textSecondary : theme.colors.text,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s ease',
    minWidth: '125px',
    width: '125px',
    height: '32px',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    opacity: disabled ? 0.6 : 1,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  }

  // Input styles for edit mode - wider for AM/PM format
  const inputStyles: React.CSSProperties = {
    padding: theme.spacing.sm,
    border: `2px solid ${theme.colors.primary}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    width: '125px',
    minWidth: '125px',
    height: '32px',
    boxSizing: 'border-box',
    zIndex: 10,
    position: 'relative',
    outline: 'none'
  }

  if (isEditing && !disabled) {
    return (
      <input
        ref={inputRef}
        type="time"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={inputStyles}
        className={className}
        placeholder={placeholder}
      />
    )
  }

  return (
    <button
      onClick={handleClick}
      style={buttonStyles}
      className={className}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (!disabled) {
          const btn = e.target as HTMLButtonElement
          btn.style.backgroundColor = theme.colors.background
          btn.style.borderColor = theme.colors.border
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          const btn = e.target as HTMLButtonElement
          btn.style.backgroundColor = 'transparent'
          btn.style.borderColor = 'transparent'
        }
      }}
          >
        <span>{formatTimeDisplay(value)}</span>
        {!disabled && <Edit size={12} />}
      </button>
  )
} 